import { Button } from '@components'
import { AttachmentSelector } from '@containers/AttachmentSelector'
import type { ReportEntryAttachment } from '@query'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useMemo, useState } from 'react'
import { FaPaperclip } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AttachmentSlot } from './interactive.styled'
import { ENTRY_ATTACHMENT_BUTTON_SIZE_PX } from './periodUtils'

type Props = {
  attachments?: ReportEntryAttachment[]
}

/**
 * Paperclip trigger in an entry row. Hover (or click on touch) opens a floating panel
 * with the shared attachment selector in read-only mode.
 *
 * Clicking an attachment preview opens a modal outside this popover. Pin the popover on
 * interaction so hover-leave / outside-press against the modal does not unmount the preview.
 */
export const EntryAttachmentAction: React.FC<Props> = ({ attachments }) => {
  const [t] = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [isPinned, setIsPinned] = useState(false)

  const hasAttachments = Boolean(attachments?.length)
  const attachmentIds = useMemo(
    () => attachments?.map(attachment => attachment.id) ?? [],
    [attachments]
  )

  const title =
    attachments?.length === 1
      ? attachments[0].name
      : t('reports.interactive.attachmentCount', { count: attachments?.length ?? 0 })

  const { context, refs, strategy, x, y } = useFloating({
    open: isOpen,
    onOpenChange: open => {
      setIsOpen(open)
      if (!open) setIsPinned(false)
    },
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })]
  })

  const hover = useHover(context, {
    enabled: hasAttachments,
    // Once the user interacts (e.g. opens a file preview modal), keep the popover mounted.
    handleClose: isPinned ? null : safePolygon(),
    delay: { open: 150, close: 100 }
  })
  const click = useClick(context, { enabled: hasAttachments })
  const dismiss = useDismiss(context, {
    enabled: hasAttachments,
    outsidePress: event => {
      const target = event.target as Node | null
      if (!target) return true
      // FilePreview / Lightbox portals live outside the floating node.
      if (document.getElementById('modal-root')?.contains(target)) return false
      if (target instanceof Element && target.closest('.yarl__root')) return false
      return true
    }
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, click, dismiss])

  if (!hasAttachments) {
    return <AttachmentSlot />
  }

  return (
    <AttachmentSlot>
      <StyledAttachmentTrigger
        ref={refs.setReference}
        type="button"
        size="sm"
        isSecondary
        icon={<FaPaperclip />}
        title={title}
        aria-label={title}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        {...getReferenceProps()}
      />
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <AttachmentPopover
              ref={refs.setFloating}
              style={{
                top: y ?? 0,
                left: x ?? 0,
                position: strategy
              }}
              {...getFloatingProps({
                onPointerDown: () => setIsPinned(true)
              })}
            >
              <AttachmentSelector value={attachmentIds} disabled hideEmptyDescription />
            </AttachmentPopover>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </AttachmentSlot>
  )
}

const StyledAttachmentTrigger = styled(Button)`
  box-sizing: border-box;
  flex-shrink: 0;
  width: ${ENTRY_ATTACHMENT_BUTTON_SIZE_PX}px;
  height: ${ENTRY_ATTACHMENT_BUTTON_SIZE_PX}px;
  min-width: ${ENTRY_ATTACHMENT_BUTTON_SIZE_PX}px;
  padding: 0;
  justify-content: center;
  opacity: 0.55;

  &:hover,
  &:focus {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.iconGray};
    outline-offset: 2px;
  }
`

const AttachmentPopover = styled(motion.div).attrs({
  variants: {
    open: { opacity: 1 },
    closed: { opacity: 0 }
  },
  initial: 'closed',
  animate: 'open',
  exit: 'closed'
})`
  position: absolute;
  z-index: 9999;
  min-width: 280px;
  max-width: 360px;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  box-shadow: ${({ theme }) => theme.shadow.md};
`
